import {
  type FlashcardSavedPublic,
  FlashcardsService,
  type FlashcardsUpsertSavedFlashcardData,
} from "@/client"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { Tooltip } from "@/components/ui/tooltip"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { FaRegBookmark } from "react-icons/fa"
import { FlashcardContent } from "./FlashcardContent"

type SaveNoteCard = {
  id: string
  deck_id: string
  deck_name?: string
  front: string
  back: string
}

type FlashcardSaveNoteModalProps = {
  card: SaveNoteCard
  compact?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  triggerLabel?: string
}

export function FlashcardSaveNoteModal({
  card,
  compact = false,
  open,
  onOpenChange,
  triggerLabel = "Save note",
}: FlashcardSaveNoteModalProps) {
  const queryClient = useQueryClient()
  const [internalOpen, setInternalOpen] = useState(false)
  const [noteHtml, setNoteHtml] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [richTextUploadCount, setRichTextUploadCount] = useState(0)
  const saveButtonRef = useRef<HTMLButtonElement>(null)

  const resolvedOpen = open ?? internalOpen
  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange?.(nextOpen)
    if (open === undefined) {
      setInternalOpen(nextOpen)
    }
    if (!nextOpen) {
      setErrorMessage("")
    }
  }

  const savedQuery = useQuery({
    queryKey: ["flashcards-saved", card.id],
    enabled: resolvedOpen,
    retry: false,
    queryFn: async (): Promise<FlashcardSavedPublic | null> => {
      try {
        return await FlashcardsService.getSavedFlashcard({ cardId: card.id })
      } catch (error) {
        if (
          typeof error === "object" &&
          error !== null &&
          "status" in error &&
          error.status === 404
        ) {
          return null
        }
        throw error
      }
    },
  })

  useEffect(() => {
    if (!resolvedOpen || savedQuery.isLoading) {
      return
    }

    const savedCard = savedQuery.data
    if (savedCard) {
      setNoteHtml(savedCard.note_html ?? "")
    } else {
      setNoteHtml("")
    }
  }, [resolvedOpen, savedQuery.data, savedQuery.isLoading])

  const invalidateSavedQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["flashcards-saved"] }),
      queryClient.invalidateQueries({
        queryKey: ["flashcards-saved", card.id],
      }),
    ])
  }

  const upsertMutation = useMutation({
    mutationFn: (
      requestBody: FlashcardsUpsertSavedFlashcardData["requestBody"],
    ) =>
      FlashcardsService.upsertSavedFlashcard({
        cardId: card.id,
        requestBody,
      }),
    onSuccess: async () => {
      await invalidateSavedQueries()
      handleOpenChange(false)
    },
    onError: () => {
      setErrorMessage("Failed to save note.")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () =>
      FlashcardsService.deleteSavedFlashcard({ cardId: card.id }),
    onSuccess: async () => {
      await invalidateSavedQueries()
      handleOpenChange(false)
    },
    onError: () => {
      setErrorMessage("Failed to remove saved card.")
    },
  })

  const isUploadingImage = richTextUploadCount > 0
  const hasSavedState = Boolean(savedQuery.data)
  const isBusy =
    savedQuery.isLoading || upsertMutation.isPending || deleteMutation.isPending

  const handleSave = () => {
    if (isUploadingImage) {
      setErrorMessage("Wait for images to finish uploading before saving.")
      return
    }
    if (containsLocalImageUrls(noteHtml)) {
      setErrorMessage("Wait for images to finish uploading before saving.")
      return
    }
    setErrorMessage("")
    upsertMutation.mutate({
      is_bookmarked: true,
      note_html: noteHtml.trim() ? noteHtml : null,
    })
  }

  useEffect(() => {
    if (!resolvedOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const isTypingTarget =
        target?.isContentEditable ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select"

      if (
        isTypingTarget ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.key.toLowerCase() !== "s"
      ) {
        return
      }

      event.preventDefault()
      saveButtonRef.current?.click()
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [resolvedOpen])

  return (
    <DialogRoot
      lazyMount
      size={{ base: "sm", md: "xl" }}
      placement="center"
      open={resolvedOpen}
      onOpenChange={({ open: nextOpen }) => handleOpenChange(nextOpen)}
    >
      <Tooltip content="Bookmark this card or add a note">
        <DialogTrigger asChild>
          {compact ? (
            <IconButton aria-label="Save note" size="sm" variant="ghost">
              <Icon as={FaRegBookmark} />
            </IconButton>
          ) : (
            <Button aria-label={triggerLabel} size="sm" variant="outline">
              <FaRegBookmark />
              {triggerLabel}
            </Button>
          )}
        </DialogTrigger>
      </Tooltip>

      <DialogContent maxW="2xl">
        <DialogHeader>
          <DialogTitle>Save flashcard note</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody pb={6}>
          <VStack align="stretch" gap={4}>
            <Box
              borderWidth={1}
              borderColor="gray.200"
              _dark={{ borderColor: "gray.700", bg: "gray.800" }}
              borderRadius="lg"
              p={4}
              bg="gray.50"
            >
              <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500">
                Front
              </Text>
              <FlashcardContent content={card.front} />
              <Text
                fontSize="xs"
                fontWeight="bold"
                mt={4}
                mb={2}
                color="gray.500"
              >
                Back
              </Text>
              <FlashcardContent content={card.back} />
            </Box>

            <Checkbox checked disabled>
              Bookmark this card
            </Checkbox>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Note
              </Text>
              <RichTextEditor
                value={noteHtml}
                onChange={setNoteHtml}
                onUploadStateChange={(isUploading) => {
                  setRichTextUploadCount((count) =>
                    Math.max(0, count + (isUploading ? 1 : -1)),
                  )
                }}
                onUploadError={setErrorMessage}
                placeholder="Add your own study note"
                minHeight="180px"
              />
            </Box>

            {errorMessage ? (
              <Text color="red.500" fontSize="sm">
                {errorMessage}
              </Text>
            ) : null}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack justify="space-between" w="full">
            <Button
              variant="ghost"
              colorPalette="red"
              onClick={() => deleteMutation.mutate()}
              disabled={!hasSavedState || isBusy}
            >
              Remove saved card
            </Button>
            <Button
              ref={saveButtonRef}
              onClick={handleSave}
              loading={isBusy}
              disabled={isBusy}
            >
              Save
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
